/**
 * `useAssignForm` - headless state for the manual "assign a package to a site" form
 * (the pre-Stripe bridge). Owns the editable fields, the detail fetch that backs the
 * resolved-features and members blocks, and the PATCH.
 *
 * No JSX and no component imports: web_admin and web_crm render their own dialog chrome.
 *
 * Backend: `GET /api/admin/subscriptions/:siteId`, `PATCH /api/admin/subscriptions/:siteId`.
 */
import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import type {
  ApiClient,
  EntitlementMap,
  Locale,
  SubscriptionDetail,
  SubscriptionRow,
} from "../types";

/** ISO timestamp to the `yyyy-mm-dd` value a date input expects. */
export function toDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}

/** `yyyy-mm-dd` back to an ISO timestamp at UTC midnight; empty string becomes null. */
export function fromDateInput(v: string): string | null {
  return v ? new Date(`${v}T00:00:00.000Z`).toISOString() : null;
}

export interface UseAssignFormOptions {
  api: ApiClient;
  /**
   * Kept in the options so both apps call the hook the same way and so a future
   * translated error message needs no signature change. The current form has no
   * message of its own, so the hook does not read it - the chrome does the labelling.
   */
  locale: Locale;
  /** Table row being edited. Seeds the form and supplies `siteId` for both requests. */
  row: SubscriptionRow;
  /** Called after a successful PATCH. The chrome closes the dialog and reloads. */
  onSaved: () => void;
}

export interface UseAssignFormResult {
  planCode: string;
  setPlanCode: Dispatch<SetStateAction<string>>;
  status: string;
  setStatus: Dispatch<SetStateAction<string>>;
  cycle: string;
  setCycle: Dispatch<SetStateAction<string>>;
  /** `yyyy-mm-dd`, empty string when unset. */
  periodEnd: string;
  setPeriodEnd: Dispatch<SetStateAction<string>>;
  /** `yyyy-mm-dd`, empty string when unset. */
  trialEnd: string;
  setTrialEnd: Dispatch<SetStateAction<string>>;
  cancelAtEnd: boolean;
  setCancelAtEnd: Dispatch<SetStateAction<boolean>>;

  /** Resolved entitlements plus members. `null` while loading (or if the fetch failed). */
  detail: SubscriptionDetail | null;
  /** `detail.entitlements.features`, defaulted to `{}`. Read for the value beside a key. */
  features: EntitlementMap;
  /** Keys worth showing: `true` booleans and non-zero numbers (a 0 limit is "off"). */
  featureKeys: string[];

  saving: boolean;
  /** PATCH the row, then call `onSaved`. Rejects if the request fails. */
  save: () => Promise<void>;
}

export function useAssignForm({ api, row, onSaved }: UseAssignFormOptions): UseAssignFormResult {
  const [planCode, setPlanCode] = useState(row.planCode);
  const [status, setStatus] = useState(row.status);
  const [cycle, setCycle] = useState(row.billingCycle);
  const [periodEnd, setPeriodEnd] = useState(toDateInput(row.currentPeriodEnd));
  const [trialEnd, setTrialEnd] = useState(toDateInput(row.trialEndsAt));
  const [cancelAtEnd, setCancelAtEnd] = useState(row.cancelAtPeriodEnd);
  const [detail, setDetail] = useState<SubscriptionDetail | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<SubscriptionDetail>(`/admin/subscriptions/${row.siteId}`).then(setDetail).catch(() => {});
    // eslint-disable-next-line
  }, [row.siteId]);

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/admin/subscriptions/${row.siteId}`, {
        planCode,
        status,
        billingCycle: cycle,
        currentPeriodEnd: fromDateInput(periodEnd),
        trialEndsAt: fromDateInput(trialEnd),
        cancelAtPeriodEnd: cancelAtEnd,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  const features = detail?.entitlements.features ?? {};
  const featureKeys = Object.keys(features).filter((k) => features[k] === true || (typeof features[k] === "number" && features[k] !== 0));

  return {
    planCode, setPlanCode,
    status, setStatus,
    cycle, setCycle,
    periodEnd, setPeriodEnd,
    trialEnd, setTrialEnd,
    cancelAtEnd, setCancelAtEnd,
    detail, features, featureKeys,
    saving, save,
  };
}
